import { useEffect, useCallback, useState, useRef } from 'react'
import type { IMessage } from '@stomp/stompjs'
import {
  connectWebSocket,
  disconnectWebSocket,
  subscribe as stompSubscribe,
  unsubscribe as stompUnsubscribe,
  sendMessage,
  isWebSocketConnected,
  addReconnectCallback,
  nextMessageId,
} from '@/lib/websocket'
import { useAuthStore } from '@/stores/authStore'

/**
 * WebSocket connection hook.
 *
 * Manages the WebSocket lifecycle and provides methods for
 * subscribing to topics and sending messages.
 */

interface UseWebSocketReturn {
  isConnected: boolean
  isConnecting: boolean
  error: Error | null
  subscribe: (destination: string, callback: (message: IMessage) => void) => () => void
  unsubscribe: (destination: string) => void
  send: (destination: string, body: string) => void
  reconnect: () => Promise<void>
}

export function useWebSocket(): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(isWebSocketConnected())
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { isAuthenticated } = useAuthStore()

  // Track active subscriptions for cleanup
  const subscriptionsRef = useRef<Map<string, () => void>>(new Map())

  // Connect on mount if authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    const connect = async () => {
      setIsConnecting(true)
      setError(null)

      try {
        await connectWebSocket()
        setIsConnected(true)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Connection failed'))
        setIsConnected(false)
      } finally {
        setIsConnecting(false)
      }
    }

    connect()

    // Register reconnect callback to update state
    const unregister = addReconnectCallback(() => {
      setIsConnected(true)
      setError(null)
    })

    // Cleanup on unmount
    return () => {
      unregister()
      // Note: We don't disconnect on unmount as other components may be using it
    }
  }, [isAuthenticated])

  // Subscribe to a destination
  const subscribe = useCallback(
    (destination: string, callback: (message: IMessage) => void): (() => void) => {
      const subscription = stompSubscribe(destination, callback)

      // Create unsubscribe function
      const unsubscribeFn = () => {
        stompUnsubscribe(destination)
        subscriptionsRef.current.delete(destination)
      }

      // Track subscription
      if (subscription) {
        subscriptionsRef.current.set(destination, unsubscribeFn)
      }

      return unsubscribeFn
    },
    []
  )

  // Unsubscribe from a destination
  const unsubscribe = useCallback((destination: string) => {
    stompUnsubscribe(destination)
    subscriptionsRef.current.delete(destination)
  }, [])

  // Send a message
  const send = useCallback((destination: string, body: string) => {
    sendMessage(destination, body)
  }, [])

  // Reconnect manually
  const reconnect = useCallback(async () => {
    setIsConnecting(true)
    setError(null)

    try {
      await disconnectWebSocket()
      await connectWebSocket()
      setIsConnected(true)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Reconnection failed'))
      setIsConnected(false)
    } finally {
      setIsConnecting(false)
    }
  }, [])

  return {
    isConnected,
    isConnecting,
    error,
    subscribe,
    unsubscribe,
    send,
    reconnect,
  }
}

/**
 * Hook for subscribing to a specific STOMP destination.
 *
 * Automatically subscribes on mount and unsubscribes on unmount.
 */
export function useSubscription<T = unknown>(
  destination: string | null,
  callback: (data: T, message: IMessage) => void
): { isSubscribed: boolean } {
  const [isSubscribed, setIsSubscribed] = useState(false)
  const { subscribe, unsubscribe } = useWebSocket()
  const callbackRef = useRef(callback)

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!destination) {
      return
    }

    const handleMessage = (message: IMessage) => {
      try {
        const data = JSON.parse(message.body) as T
        callbackRef.current(data, message)
      } catch (error) {
        console.error(`[useSubscription] Failed to parse message from ${destination}:`, error)
      }
    }

    const unsubscribeFn = subscribe(destination, handleMessage)
    setIsSubscribed(true)

    return () => {
      unsubscribeFn()
      setIsSubscribed(false)
    }
  }, [destination, subscribe, unsubscribe])

  return { isSubscribed }
}

/**
 * Hook for making request-response style calls over WebSocket.
 *
 * This mirrors v1's consolesend pattern where we subscribe to a
 * response topic, make a request, and wait for the response.
 */
export function useWebSocketRequest() {
  const { subscribe, send } = useWebSocket()

  const request = useCallback(
    async <T>(requestTopic: string, message: string): Promise<T> => {
      const id = nextMessageId()
      const responseTopic = `/user/topic/wsconsole/${id}`

      return new Promise<T>((resolve, reject) => {
        let cleanup: (() => void) | null = null

        const timeout = setTimeout(() => {
          cleanup?.()
          reject(new Error('Request timeout'))
        }, 30000)

        cleanup = subscribe(responseTopic, (response) => {
          clearTimeout(timeout)
          cleanup?.()

          try {
            const data = JSON.parse(response.body) as T
            resolve(data)
          } catch {
            reject(new Error('Failed to parse response'))
          }
        })

        // Send the request
        send(requestTopic, message)
      })
    },
    [subscribe, send]
  )

  return { request }
}
